/**
 * パスキー（/money/passkeys）: 出金の確認に使うパスキーの一覧・追加・削除。
 * サーバー: GET/POST /v1/money/passkeys・POST …/options・POST …/:id/revoke-options・DELETE …/:id（money/passkeys.ts・lib/webauthn.ts）。
 *
 * 追加には本人の確認（パスワードの再入力か OAuth のやり直し・サーバーが強制）が要る: ロックしていない PC を他人に使われても、その人のパスキーを足されない。
 * **2 つ目以降の追加は既存のパスキーでの確認も要る**（SEC-ACCESS-05・2026-09-27 payout-security）: メールを取られてパスワードを
 * 再設定されても、パスワードだけでは攻撃者のパスキーを足せない。options の `confirm` があれば、作る前に既存のパスキーで確認を取る。
 * **削除はパスワード / OAuth のやり直しか既存のパスキーでの確認が要る**（SEC-ACCESS-19。最後の 1 本はパスキーだけ = N1）: 開きっぱなしの画面から本人のパスキーを消されない。
 * 追加から一定時間（いまは 72 時間・サーバーの cooldownUntil が正）は live の出金を確認できない（乗っ取り → パスキー追加 → 即出金 を防ぐ）
 * ので、一覧に「いつから使えるか」を出す。
 *
 * 2026-09-27 money/oauth-reauth: サーバーは追加・削除の再認証に password の代わりに reauthToken（OAuth のやり直し・Core の lib/developerReauth.ts）も
 * 受けるようになった（OAuth だけでパスワードを持たない開発者のため）。再認証の失敗は 400 reauthentication_required / 401 reauthentication_failed
 * （details = { hasPassword, providers }）。
 *
 * 2026-09-28 money/reauth-ui: 画面の再認証を Creator の部品（components/Reauth.tsx の useReauth）に置き換えた。
 *   WHY: 今まではこの画面が自前のパスワード欄だけを持っていた = Google 等だけで登録した人（パスワードなし）はパスキーを作れない = 出金できない。
 *   部品のダイアログは { hasPassword, providers } を見て「パスワード欄」か「Google で確認」等のボタンを出し分ける。パスワード管理の
 *   自動入力の書き方（post のフォーム・隠しのユーザー名・current-password・id / name / label）も部品が持つ（authForms.ts）ので、
 *   この画面のパスワード欄（と隠しのユーザー名）は外した。
 *   - 追加: 「次へ」のクリックの中で、戻ってきた証明（takePending）か部品のダイアログで証明を得る → options →（既存があれば）既存のパスキーで確認
 *     → navigator.credentials.create → POST。**「Google で確認」から戻っても自動では続けない**（WebAuthn の create はユーザーの操作の中でしか
 *     呼べない）= 戻った画面に「確認済みです。もう一度『次へ』を押してください」を出し、追加の欄を開いておく
 *   - 削除: パスキーでの確認（assertion）は今までどおり。もう一方の選択肢を「パスワードか Google 等」（部品）にした。
 *     「Google で確認」から戻ったら action（passkey:del:<id>）で対象を復元して**自動で続ける**（WebAuthn を使わない = 操作が要らない）
 *   - 方法（{ hasPassword, providers }）は画面の読み込み時に GET /v1/developers/me/identities から（passkeyReauth.ts の passkeyReauthMethods）
 *   - reauthToken はメモリだけ（部品の約束・devOAuthClient.ts）。この画面も URL・ストレージに置かない
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, ApiError } from '../../api';
import { isReauthError, useReauth, useReauthFailureText } from '../../components/Reauth';
import { ago, ConfirmDialog, Empty, ErrorState, fullDate, PageHeader, Spinner } from '../../components/ui';
import type { ReauthMethods, ReauthProof } from '../../devOAuth';
import { useResource } from '../../hooks';
import { errorText, useT } from '../../i18n';
import type { AssertionOptionsJson, PasskeyInfo, PasskeyRegistrationOptions } from '../../moneyApi';
import { liveConfirmReadiness, passkeyLiveReadyAt } from '../../moneyLogic';
import { createPasskey, getPasskeyAssertion, isPasskeyCancel, passkeysSupported } from '../../passkey';
import { useSession } from '../../session';
import { asReauthRequired, isPasskeyAction, PASSKEY_ADD_ACTION, passkeyReauthMethods, passkeyRemoveAction, removalChoices, removalIdFromAction } from './passkeyReauth';

type Reauth = ReturnType<typeof useReauth>;
/** GET /v1/developers/me/identities の中で使う所だけ（Core の modules/platform/oauth/identities.ts が正） */
type IdentitiesResponse = { hasPassword?: boolean; identities?: Array<{ provider: string }> };

export function PasskeysPage() {
  const r = useResource<{ passkeys: PasskeyInfo[] }>('/v1/money/passkeys');
  // 確認の方法を先に知る（passkeyReauth.ts の passkeyReauthMethods の WHY）。読めなくても画面は動く（セッションの hasPassword で補う）
  const ids = useResource<IdentitiesResponse>('/v1/developers/me/identities');
  // ログイン用パスキーがあれば「パスキーで確認」も出す（passkeyReauthMethods の第 3 引数）。読めなくても画面は動く（0 件扱い）
  const loginPks = useResource<{ passkeys: unknown[] }>('/v1/developers/me/login-passkeys');
  const { developer } = useSession();
  const { t } = useT();
  const reauth = useReauth();
  const failureText = useReauthFailureText();
  // 「Google で確認」から戻って追加の途中 = 追加の欄を開いた状態で始める（もう一度「次へ」を押してもらう）
  const [adding, setAdding] = useState(() => reauth.pending?.action === PASSKEY_ADD_ACTION);
  const [removing, setRemoving] = useState<PasskeyInfo | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  const list = r.data?.passkeys ?? [];
  const methods: ReauthMethods = passkeyReauthMethods(ids.data, developer?.hasPassword, loginPks.data?.passkeys.length ?? 0);
  // identities が読めた = 方法が確か → 送る前に部品のダイアログで聞く（往復を 1 回減らす）。読めない → 送ってサーバーの details に従う
  const methodsKnown = !!ids.data && typeof ids.data.hasPassword === 'boolean';
  const choices = removalChoices(passkeysSupported(), list.length);
  const [removeWith, setRemoveWith] = useState<'passkey' | 'reauth'>('passkey');

  const openRemove = (p: PasskeyInfo) => {
    setRemoving(p);
    // 既定はパスキー（持っている証明 = いちばん強い・今までどおり）。使えない端末では再認証
    setRemoveWith(choices[0] ?? 'passkey');
    setErr(null);
  };

  const removed = () => {
    setRemoving(null);
    setNotice({ tone: 'ok', text: t('pk.removed') });
    r.reload();
  };

  /**
   * 削除（パスワードか OAuth のやり直し）: 部品の run が「戻ってきた証明（同じ action）があればそれ → 無ければダイアログ → 送る →
   * 401 reauthentication_failed ならもう一度ダイアログ」を回す。取り消したら null（何もしない）。
   * 最後の 1 本はこの経路を出さない（removalChoices）が、出てもサーバーが 403 passkey_assertion_required を**再認証より先に**返す = 証明は燃えない
   */
  const removeWithReauth = async (id: string) => {
    setBusy(true);
    setErr(null);
    try {
      const done = await reauth.run(
        (proof) =>
          api(`/v1/money/passkeys/${id}`, { method: 'DELETE', body: { ...proof } }).catch((e: unknown) => {
            // 方法が分からずに証明なしで送った場合の 403（details つき）→ 部品がダイアログを出せる形に読み替える（passkeyReauth.ts）
            throw asReauthRequired(e);
          }),
        { action: passkeyRemoveAction(id), ...(methodsKnown ? { ask: methods } : {}) }
      );
      if (done !== null) removed();
    } catch (e) {
      const text = e instanceof ApiError ? errorText(e) : String(e);
      // ConfirmDialog が閉じている（戻ってきて自動で続けた）ときは画面の上に出す
      if (removing) setErr(text);
      else setNotice({ tone: 'error', text });
    } finally {
      setBusy(false);
    }
  };

  /** 削除（パスキー）: revoke-options で challenge を取り（対象のパスキーに結び付く）→ navigator.credentials.get → 本文に assertion。今までどおり */
  const removeWithPasskey = async (id: string) => {
    setBusy(true);
    setErr(null);
    try {
      const { publicKey } = await api<{ publicKey: AssertionOptionsJson }>(`/v1/money/passkeys/${id}/revoke-options`, { method: 'POST', body: {} });
      await api(`/v1/money/passkeys/${id}`, { method: 'DELETE', body: { assertion: await getPasskeyAssertion(publicKey) } });
      removed();
    } catch (e) {
      if (isPasskeyCancel(e)) setErr(t('pk.cancelled'));
      else setErr(e instanceof ApiError ? errorText(e) : String(e));
    } finally {
      setBusy(false);
    }
  };

  // 「Google で確認」から戻った削除は、action の ID で対象を復元して自動で続ける（1 回だけ = StrictMode の二重実行・再描画で二度送らない）。
  // 一覧に無い（もう消えている・別のアカウント）→ 証明を捨てる（使わない token を持ち続けない）
  const resumedOnce = useRef(false);
  useEffect(() => {
    const id = removalIdFromAction(reauth.pending?.action);
    if (!id || resumedOnce.current || !r.data) return;
    resumedOnce.current = true;
    if (r.data.passkeys.some((p) => p.id === id)) void removeWithReauth(id);
    else reauth.consume();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reauth.pending, r.data]);

  // 戻りで失敗した（別の Google アカウントを選んだ・キャンセル）: この画面の操作のものだけ出して捨てる
  useEffect(() => {
    const f = reauth.failure;
    if (!f || !isPasskeyAction(f.action)) return;
    setNotice({ tone: 'error', text: failureText(f) });
    if (f.action === PASSKEY_ADD_ACTION) setAdding(true);
    reauth.consume();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reauth.failure]);

  const readiness = r.data ? liveConfirmReadiness(list, new Date()) : null;
  const addResumed = reauth.pending?.action === PASSKEY_ADD_ACTION;
  return (
    <>
      <PageHeader title={t('pk.title')}>
        {!adding && passkeysSupported() && (
          <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}>
            {t('pk.add')}
          </button>
        )}
      </PageHeader>
      <p className="page-lead muted">{t('pk.lead')}</p>
      {!passkeysSupported() && (
        <div className="alert alert-warn" role="note">
          {t('pk.unsupported')}
        </div>
      )}
      {notice && (
        <div className={`alert ${notice.tone === 'ok' ? 'alert-ok' : 'alert-error'}`} role={notice.tone === 'ok' ? 'status' : 'alert'}>
          {notice.text}
        </div>
      )}
      {adding && (
        <AddPasskey
          hasExisting={list.length > 0}
          reauth={reauth}
          methods={methods}
          resumed={addResumed}
          onCancel={() => {
            setAdding(false);
            // 追加をやめた = 戻ってきた証明も使わない（5 分で切れるが、押せるボタンの前提を残さない）
            if (addResumed) reauth.consume();
          }}
          onAdded={(p) => {
            setAdding(false);
            setNotice({ tone: 'ok', text: t('pk.added', { when: fullDate(passkeyLiveReadyAt(p).toISOString()) }) });
            r.reload();
          }}
        />
      )}
      {r.loading && !r.data && <Spinner />}
      {r.error && <ErrorState error={r.error} what={t('pk.title')} onRetry={r.reload} />}
      {readiness?.state === 'later' && (
        <div className="alert alert-info" role="note">
          {t('pk.cooldownAll', { when: fullDate(readiness.at.toISOString()) })}
        </div>
      )}
      {r.data && list.length === 0 && !adding && (
        <section className="card">
          <Empty title={t('pk.emptyTitle')} icon="lock">
            <p>{t('pk.emptyBody')}</p>
          </Empty>
        </section>
      )}
      {list.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>{t('pk.col.name')}</th>
                <th>{t('pk.col.added')}</th>
                <th>{t('pk.col.lastUsed')}</th>
                <th>{t('pk.col.live')}</th>
                <th>
                  <span className="sr-only">{t('pk.remove')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {list.map((p) => {
                const readyAt = passkeyLiveReadyAt(p);
                const ready = readyAt.getTime() <= Date.now();
                return (
                  <tr key={p.id}>
                    <td>
                      <strong>{p.label}</strong>
                    </td>
                    <td title={fullDate(p.createdAt)}>{ago(p.createdAt)}</td>
                    <td>{p.lastUsedAt ? ago(p.lastUsedAt) : <span className="muted">{t('common.never')}</span>}</td>
                    <td>
                      <span className={`pill ${ready ? 'pill-ok' : 'pill-warn'}`}>
                        {ready ? t('pk.liveReady') : t('pk.liveFrom', { when: fullDate(readyAt.toISOString()) })}
                      </span>
                    </td>
                    <td className="cell-actions">
                      <button type="button" className="btn btn-ghost btn-sm btn-danger-text" onClick={() => openRemove(p)}>
                        {t('pk.remove')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <ConfirmDialog
        open={!!removing}
        title={t('pk.removeTitle', { label: removing?.label ?? '' })}
        confirmLabel={t('pk.remove')}
        tone="danger"
        busy={busy}
        confirmDisabled={choices.length === 0}
        onClose={() => {
          setRemoving(null);
          setErr(null);
        }}
        onConfirm={() => {
          if (!removing) return;
          void (removeWith === 'passkey' ? removeWithPasskey(removing.id) : removeWithReauth(removing.id));
        }}
      >
        <p>{t('pk.removeBody')}</p>
        {choices.length > 0 && (
          <fieldset className="choice-group">
            <legend>{t('pk.removeVerify')}</legend>
            {choices.includes('passkey') && (
              <label className={`choice ${removeWith === 'passkey' ? 'is-active' : ''}`}>
                <input type="radio" name="remove-with" value="passkey" checked={removeWith === 'passkey'} onChange={() => setRemoveWith('passkey')} />
                <span>
                  <strong>{t('pk.removeWithPasskey')}</strong>
                  <small className="muted">{t('pk.removeWithPasskeyHint')}</small>
                </span>
              </label>
            )}
            {choices.includes('reauth') && (
              <label className={`choice ${removeWith === 'reauth' ? 'is-active' : ''}`}>
                <input type="radio" name="remove-with" value="reauth" checked={removeWith === 'reauth'} onChange={() => setRemoveWith('reauth')} />
                <span>
                  <strong>{t('pk.removeWithReauth')}</strong>
                  <small className="muted">{t('pk.removeWithReauthHint')}</small>
                </span>
              </label>
            )}
          </fieldset>
        )}
        {/* 最後の 1 本はパスキーでしか消せない（サーバーの N1）。パスキーが使えない端末では消せないことを先に言う */}
        {list.length <= 1 && (
          <p className={`small ${choices.length === 0 ? 'alert alert-warn' : 'muted'}`}>{t(choices.length === 0 ? 'pk.removeLastUnsupported' : 'pk.removeLastPasskeyOnly')}</p>
        )}
        {err && (
          <p className="alert alert-error" role="alert">
            {err}
          </p>
        )}
      </ConfirmDialog>
      {/* 部品のダイアログは ConfirmDialog の外に置く（<form> の入れ子にしない = 部品のフォームがパスワード管理に正しく見える）。
          どちらも showModal なので、後から開いた部品のダイアログが上に重なる */}
      {reauth.dialog}
    </>
  );
}

function AddPasskey({
  hasExisting,
  reauth,
  methods,
  resumed,
  onCancel,
  onAdded
}: {
  hasExisting: boolean;
  reauth: Reauth;
  methods: ReauthMethods;
  /** 「Google で確認」から戻ってきた（証明がメモリにある）= もう一度「次へ」を押せば続く */
  resumed: boolean;
  onCancel: () => void;
  onAdded: (p: PasskeyInfo) => void;
}) {
  const { t } = useT();
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // 「パスキーで確認」（ログイン用パスキー）で得た証明を、次のクリックまで持つ（2026-09-28 Creator の依頼）。
  // WHY: Safari は 1 回のユーザー操作で WebAuthn を 2 つ（確認の get と作成の create）呼ぶのを断ることがある = 確認の直後に create を続けない。
  //   「Google で確認」から戻ったとき（pk.reauthResumed）と同じく、もう一度「次へ」を押してもらう。証明はメモリだけ（5 分・一回限り）
  const [heldToken, setHeldToken] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      // 1. 本人の確認を**最初に**（WebAuthn の create より前）: 戻ってきた証明（「Google で確認」の後・一回限り）か、部品のダイアログ。
      //    WHY 先か: create の後にパスワード違いが分かると、認証器に使われないパスキーが残る。取り消し（null）なら何も作らない。
      //    ダイアログの「確認」もクリック = この後の create はユーザーの操作の続きとして呼べる（今までの「送信 → options → create」と同じ間合い）。
      //    「Google で確認」を押すとプロバイダの画面へ移る = この Promise は宙に残らず、画面ごと消える（部品が returnTo = この画面へ戻す）
      const token = heldToken ?? reauth.takePending(PASSKEY_ADD_ACTION);
      if (heldToken) setHeldToken(null);
      let proof: ReauthProof | null = token ? { reauthToken: token } : await reauth.requestReauth(methods, { action: PASSKEY_ADD_ACTION });
      if (!proof) return;
      // ダイアログから証明の token が直接返った = 「パスキーで確認」（WebAuthn を使った）。パスワードなら { password }、
      // 「Google で確認」は画面を離れるのでここには返らない。この場合は同じクリックで create を呼ばず、もう一度押してもらう（上の WHY）
      if (!token && 'reauthToken' in proof && proof.reauthToken) {
        setHeldToken(proof.reauthToken);
        return;
      }
      // 2. options（challenge）→（既にパスキーがあれば）既存のパスキーで確認 → 認証器で新しいのを作る。
      //    確認を作成より先にする理由: 既存のパスキーを持っていない人（乗っ取り）に、新しいパスキーを作らせる手間すら掛けさせない・
      //    本人には「まず今のパスキーで確認 → 新しいのを作る」の 2 回のプロンプトになる（画面に予告を出す = pk.confirmFirst）。challenge はどちらも 5 分
      const opts = await api<PasskeyRegistrationOptions>('/v1/money/passkeys/options', { method: 'POST', body: {} });
      const confirmation = opts.confirm ? await getPasskeyAssertion(opts.confirm) : undefined;
      const credential = await createPasskey(opts.publicKey);
      // 3. 送る。パスワード違い（401 reauthentication_failed）・証明切れなら、同じ credential / confirmation のまま証明だけ取り直して送り直す。
      //    サーバーは再認証を challenge の消費・確認の検証より**前**に判定する（passkeys.ts の finishRegistration）= 失敗しても challenge は
      //    未使用のまま = 作ったパスキーを捨てずに済む。回数の上限はサーバー（打ち間違いの制限）が正、ここは無限に回らないためだけ
      for (let i = 0; i < 5 && proof; i++) {
        try {
          const res = await api<{ passkey: PasskeyInfo }>('/v1/money/passkeys', {
            method: 'POST',
            body: { ...proof, credential, label: label.trim() || undefined, ...(confirmation ? { confirmation } : {}) }
          });
          onAdded(res.passkey);
          return;
        } catch (e2) {
          if (!isReauthError(e2)) throw e2;
          proof = await reauth.requestReauth(e2 as ApiError, { action: PASSKEY_ADD_ACTION });
        }
      }
    } catch (e2) {
      if (isPasskeyCancel(e2)) setErr(t('pk.cancelled'));
      else setErr(e2 instanceof ApiError ? errorText(e2) : String(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="card" onSubmit={submit}>
      <h2 className="card-title">{t('pk.addTitle')}</h2>
      {(resumed || heldToken) && (
        <p className="alert alert-ok" role="status">
          {t('pk.reauthResumed')}
        </p>
      )}
      {hasExisting && <p className="small muted">{t('pk.confirmFirst')}</p>}
      <div className="form-grid form-grid-top">
        <label className="field">
          <span>
            {t('pk.label')}
            <small>{t('common.optional')}</small>
          </span>
          {/* autocomplete off: 名前の欄をユーザー名と取り違えてパスワード管理が保存しないように */}
          <input name="passkey-label" autoComplete="off" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={60} placeholder={t('pk.labelPlaceholder')} />
        </label>
      </div>
      {!resumed && <p className="small muted">{t('pk.passwordHint')}</p>}
      {err && (
        <p className="alert alert-error" role="alert">
          {err}
        </p>
      )}
      <div className="form-actions">
        <button type="button" className="btn btn-ghost" onClick={onCancel}>
          {t('common.cancel')}
        </button>
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? t('common.working') : t('pk.addConfirm')}
        </button>
      </div>
    </form>
  );
}
